import React, {useState, useMemo, useEffect, ReactNode} from 'react';
import Router from 'universal-router';
import {UrlParams} from 'universal-router/generateUrls';
import {BrowserHistory} from 'history';
import {RouterReactContext} from './RouterReactContext';
import {RouteReactContext} from './RouteReactContext';

interface BrowserRouterProviderProps<RouteResult = any> {
	initialRoute: RouteResult;
	router: Router;
	history: BrowserHistory;
	urlGenerator(routeName: string, params?: UrlParams): string;
	routeResolver(url: string): Promise<RouteResult>;
	onChange?(route: RouteResult, pathname: string): void;
	children: ReactNode;
}

export const BrowserRouterProvider = <RouteResult extends any>(props: BrowserRouterProviderProps<RouteResult>) => {
	const {history, router, initialRoute, urlGenerator, routeResolver, onChange, children} = props;

	const [route, setRoute] = useState(initialRoute);

	const routerContext = useMemo(() => {
		return {router, history, urlGenerator};
	}, [router, history, urlGenerator]);

	useEffect(() => {
		return history.listen(async update => {
			// TODO maybe ignore when path is not changed
			// TODO handle concurrency
			const pathname = update.location.pathname;
			const newUrl = decodeURIComponent(history.createHref({pathname}));
			const newRoute = await routeResolver(newUrl);

			// Stop if location was changed during data loading
			if (location.pathname !== history.location.pathname) {
				return;
			}

			setRoute(newRoute);
			if (onChange) {
				onChange(newRoute, pathname);
			}
		});
	}, [history, routeResolver]);

	return (
		<RouterReactContext.Provider value={routerContext}>
			<RouteReactContext.Provider value={route}>{children}</RouteReactContext.Provider>
		</RouterReactContext.Provider>
	);
};

BrowserRouterProvider.displayName = 'BrowserRouterProvider';
